import { isEmpty, kebabCase, lowerCase } from 'lodash';
import { useIntl } from 'react-intl';

import { ConsultType, TemplateFrequency } from '../../data/types/api/graphql';

export type AgendaAppointmentDescriptionData = {
  startDate: string;
  endDate: string;
  type: ConsultType | undefined;
  frequency: TemplateFrequency;
};

type AgendaAppointmentDescriptionProps = {
  data: AgendaAppointmentDescriptionData;
};

export function AgendaAppointmentDescription({
  data,
}: AgendaAppointmentDescriptionProps) {
  const intl = useIntl();

  if (isEmpty(data.startDate)) return null;

  const type = data.type ?? ConsultType.Physical;

  let values = {
    ...data,
    type: intl.formatMessage({
      id: `common.agenda-appointment-type.${kebabCase(type)}`,
    }),
    frequency: intl.formatMessage({
      id: `common.agenda-template-frequency.${kebabCase(data.frequency)}`,
    }),
    date: intl.formatDate(data.startDate, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
    start: intl.formatDate(data.startDate, {
      hour: 'numeric',
      minute: 'numeric',
    }),
    end: intl.formatDate(data.endDate, {
      hour: 'numeric',
      minute: 'numeric',
    }),
  };

  if (data.frequency !== TemplateFrequency.Once) {
    values = { ...values, type: lowerCase(values.type) };

    if (isEmpty(data.endDate)) {
      // case 4 {frequency} {type} op {date} om {start} zonder einddatum.
      return intl.formatMessage(
        {
          id: 'common.appointment-description.case-4',
        },
        values
      );
    }

    // case 5 {frequency} {type} op {date} om {start} met einddatum {endDate}.
    return intl.formatMessage(
      {
        id: 'common.appointment-description.case-5',
      },
      {
        ...values,
        endDate: intl.formatDate(data.endDate, {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }),
      }
    );
  }

  if ([ConsultType.Else, ConsultType.Exception].includes(type)) {
    // case 3 {type} op {date} van {start} tot {end}.
    return intl.formatMessage(
      {
        id: 'common.appointment-description.case-3',
      },
      values
    );
  }

  if (
    [ConsultType.Telephone, ConsultType.Visit].includes(type) ||
    (data.type &&
      [ConsultType.Telephone, ConsultType.Visit].includes(data.type))
  ) {
    // case 2 {type} op {date} tussen {start} en {end}.
    return intl.formatMessage(
      {
        id: 'common.appointment-description.case-2',
      },
      values
    );
  }

  // case 1 {type} op {date} om {start}.
  return intl.formatMessage(
    {
      id: 'common.appointment-description.case-3',
    },
    values
  );
}
